import React, { ChangeEvent, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { FileDownload } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  RentalImportBatchSummary,
  useCommitRentalImport,
  useRentalImportStatus,
  useRetryRentalImportRow,
} from '~/hooks/apis/vendors/useBulkUploadRentals';

import BatchHistoryTable from './BatchHistoryTable';
import BulkUploadRentalsTracker from './BulkUploadRentalsTracker';

export type BulkUploadStage = 'upload' | 'tracking';

const TEMPLATE_HEADER = 'Vendor ID,Offer ID,Hotel Code';
const DEFAULT_COMMISSION = 15;

interface Props {
  batches: Array<RentalImportBatchSummary>;
  currentUserId?: string;
  isBatchesError?: boolean;
  isBatchesLoading: boolean;
  isUserLoading?: boolean;
  onClose: () => void;
  onStageChange?: (stage: BulkUploadStage) => void;
  onBack?: () => void;
  onRequestClose?: (requestClose: () => void) => void;
}

export default function BulkUploadRentalsContent({
  batches,
  currentUserId,
  isBatchesError,
  isBatchesLoading,
  isUserLoading,
  onClose,
  onStageChange,
  onBack,
  onRequestClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [commission, setCommission] = useState(DEFAULT_COMMISSION);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [lookupBatchId, setLookupBatchId] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);

  const commitMutation = useCommitRentalImport();
  const statusQuery = useRentalImportStatus(batchId);
  const retryMutation = useRetryRentalImportRow();

  const isBusy = commitMutation.isPending || (isLookingUp && statusQuery.isLoading);
  const isTracking = commitMutation.isSuccess || (isLookingUp && !!statusQuery.data);
  const stage: BulkUploadStage = isTracking ? 'tracking' : 'upload';

  useEffect(() => {
    onStageChange?.(stage);
  }, [stage, onStageChange]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([TEMPLATE_HEADER], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-rentals-upload-template.csv';
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  const handleCommit = () => {
    if (!file) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
      return;
    }
    commitMutation.mutate(
      { file, commission },
      {
        onSuccess: (data) => {
          setIsLookingUp(false);
          setBatchId(data.result.batch_id);
          queryClient.invalidateQueries({ queryKey: ['vendors', 'rental-import', 'batches'] });
        },
        onError: (error: Error) => {
          enqueueSnackbar(`Upload failed: ${error.message}`, { variant: 'error' });
        },
      },
    );
  };

  const handleLookup = () => {
    if (!lookupBatchId.trim()) {
      return;
    }
    setIsLookingUp(true);
    setBatchId(lookupBatchId.trim());
  };

  const handleBackToUpload = () => {
    setIsLookingUp(false);
    setBatchId(null);
    commitMutation.reset();
  };

  const handleSelectBatch = (selectedBatchId: string) => {
    setIsLookingUp(true);
    setBatchId(selectedBatchId);
  };

  const handleRetry = (rowNumber: number) => {
    if (!batchId) {
      return;
    }
    retryMutation.mutate(
      { batchId, rowNumber },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['vendors', 'rental-import', batchId] });
        },
        onError: (error: Error) => {
          enqueueSnackbar(`Retry failed: ${error.message}`, { variant: 'error' });
        },
      },
    );
  };

  const status = statusQuery.data?.result;

  if (status) {
    return (
      <BulkUploadRentalsTracker
        status={status}
        onRetry={handleRetry}
        retryingRowNumber={retryMutation.isPending ? retryMutation.variables?.rowNumber : undefined}
        onBack={handleBackToUpload}
        onClose={onClose}
        onRequestClose={onRequestClose}
      />
    );
  }

  if (isTracking && statusQuery.isError) {
    return (
      <DialogContent>
        <Stack spacing={2} alignItems="flex-start" py={2}>
          <Alert severity="error">Failed to load import status: {statusQuery.error.message}</Alert>
          <Button variant="outlined" onClick={() => statusQuery.refetch()}>
            Retry
          </Button>
        </Stack>
      </DialogContent>
    );
  }

  if (isTracking) {
    return (
      <DialogContent>
        <Stack alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={20} />
        </Stack>
      </DialogContent>
    );
  }

  return (
    <>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {commitMutation.isPending && (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              Uploading file, please wait...
            </Alert>
          )}

          {commitMutation.isError && <Alert severity="error">Upload failed: {commitMutation.error.message}</Alert>}

          {!commitMutation.isPending && (
            <>
              <Alert severity="info">
                Upload a CSV with Vendor ID, Offer ID, and Hotel Code columns. Each row creates one rental &ndash; max
                500 rows per upload.
              </Alert>
              <Stack spacing={1}>
                <Typography variant="body2">
                  Need the template? Download it first, fill in your data, then upload:
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={handleDownloadTemplate}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Download template (.csv)
                </Button>
              </Stack>
              <Typography variant="body2">Select a CSV file to upload:</Typography>
              <TextField
                type="file"
                onChange={handleFileChange}
                disabled={commitMutation.isPending}
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: '.csv' }}
                fullWidth
              />
              <TextField
                label="Commission"
                type="number"
                value={commission}
                onChange={(event) => setCommission(Math.min(100, Math.max(0, Number(event.target.value))))}
                disabled={commitMutation.isPending}
                InputProps={{ endAdornment: '%', inputProps: { min: 0, max: 100 } }}
                sx={{ maxWidth: 200 }}
              />

              <Divider />

              <Stack spacing={1}>
                <Typography variant="body2" color="text.primary">
                  Previous batches (kept for <b>24 hours</b>):
                </Typography>
                <BatchHistoryTable
                  batches={batches}
                  currentUserId={currentUserId}
                  isError={isBatchesError}
                  isLoading={isBatchesLoading}
                  isUserLoading={isUserLoading}
                  onSelectBatch={handleSelectBatch}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body2" color="text.primary">
                  Or look up a batch by ID:
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    placeholder="Batch ID"
                    size="small"
                    fullWidth
                    value={lookupBatchId}
                    onChange={(event) => setLookupBatchId(event.target.value)}
                    disabled={isLookingUp && statusQuery.isLoading}
                    error={isLookingUp && statusQuery.isError}
                    helperText={isLookingUp && statusQuery.isError ? 'Batch not found' : undefined}
                  />
                  <Button
                    variant="text"
                    onClick={handleLookup}
                    disabled={!lookupBatchId.trim() || (isLookingUp && statusQuery.isLoading)}
                    sx={{ flexShrink: 0, mt: 0.5 }}
                  >
                    {isLookingUp && statusQuery.isLoading ? <CircularProgress size={20} /> : 'View status'}
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: onBack && stage === 'upload' ? 'space-between' : 'flex-end' }}>
        {onBack && stage === 'upload' && (
          <Button variant="outlined" onClick={onBack} disabled={isBusy}>
            Single upload
          </Button>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="text" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCommit} disabled={!file || commitMutation.isPending}>
            {commitMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {commitMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
}
